import { useCallback, useEffect, useReducer, useRef } from "react";
import type {
  UploadFormAction,
  UploadFormState,
  UploadMethod,
} from "@/types/knowledge/knowledge-acquisition";

const createInitialState = (
  defaultAgent: string,
  defaultMethod: UploadMethod = "pdf",
): UploadFormState => ({
  activeMethod: defaultMethod,
  pdfUrl: "",
  pdfFile: null,
  youtubeUrl: "",
  websiteUrl: "",
  scrapeUrl: "",
  audioFile: null,
  documentFile: null,
  imageFiles: [],
  videoFile: null,
  videoUrl: "",
  researchQuery: "",
  researchTitle: "",
  selectedAgentId: defaultAgent,
});

const resetMethodState = (state: UploadFormState): UploadFormState => ({
  ...state,
  pdfUrl: "",
  pdfFile: null,
  youtubeUrl: "",
  websiteUrl: "",
  scrapeUrl: "",
  audioFile: null,
  documentFile: null,
  imageFiles: [],
  videoFile: null,
  videoUrl: "",
  researchQuery: "",
  researchTitle: "",
});

const formReducer = (state: UploadFormState, action: UploadFormAction): UploadFormState => {
  switch (action.type) {
    case "SET_ACTIVE_METHOD":
      return {
        ...resetMethodState(state),
        activeMethod: action.payload,
      };
    case "SET_PDF_URL":
      return { ...state, pdfUrl: action.payload };
    case "SET_PDF_FILE":
      return { ...state, pdfFile: action.payload };
    case "SET_YOUTUBE_URL":
      return { ...state, youtubeUrl: action.payload };
    case "SET_WEBSITE_URL":
      return { ...state, websiteUrl: action.payload };
    case "SET_SCRAPE_URL":
      return { ...state, scrapeUrl: action.payload };
    case "SET_AUDIO_FILE":
      return { ...state, audioFile: action.payload };
    case "SET_DOCUMENT_FILE":
      return { ...state, documentFile: action.payload };
    case "SET_IMAGE_FILES":
      return { ...state, imageFiles: action.payload };
    case "SET_VIDEO_FILE":
      return { ...state, videoFile: action.payload };
    case "SET_VIDEO_URL":
      return { ...state, videoUrl: action.payload };
    case "SET_RESEARCH_QUERY":
      return { ...state, researchQuery: action.payload };
    case "SET_RESEARCH_TITLE":
      return { ...state, researchTitle: action.payload };
    case "SET_SELECTED_AGENT_ID":
      return { ...state, selectedAgentId: action.payload };
    case "SYNC_DEFAULTS": {
      const nextState =
        state.activeMethod === action.payload.initialMethod ? state : resetMethodState(state);
      return {
        ...nextState,
        activeMethod: action.payload.initialMethod,
        selectedAgentId: action.payload.defaultAgentId,
      };
    }
    case "RESET_METHOD_STATE":
      return resetMethodState(state);
    default:
      return state;
  }
};

export const useUploadFormState = (
  defaultAgentId: string,
  initialMethod: UploadMethod = "pdf",
) => {
  const [state, dispatch] = useReducer(
    formReducer,
    createInitialState(defaultAgentId, initialMethod),
  );
  const previousDefaults = useRef({ defaultAgentId, initialMethod });

  useEffect(() => {
    const previous = previousDefaults.current;
    if (
      previous.defaultAgentId === defaultAgentId &&
      previous.initialMethod === initialMethod
    ) {
      return;
    }

    previousDefaults.current = { defaultAgentId, initialMethod };
    dispatch({
      type: "SYNC_DEFAULTS",
      payload: { defaultAgentId, initialMethod },
    });
  }, [defaultAgentId, initialMethod]);

  return {
    state,
    setActiveMethod: useCallback((method: UploadMethod) => {
      dispatch({ type: "SET_ACTIVE_METHOD", payload: method });
    }, []),
    setPdfUrl: useCallback((url: string) => {
      dispatch({ type: "SET_PDF_URL", payload: url });
    }, []),
    setPdfFile: useCallback((file: File | null) => {
      dispatch({ type: "SET_PDF_FILE", payload: file });
    }, []),
    setYoutubeUrl: useCallback((url: string) => {
      dispatch({ type: "SET_YOUTUBE_URL", payload: url });
    }, []),
    setWebsiteUrl: useCallback((url: string) => {
      dispatch({ type: "SET_WEBSITE_URL", payload: url });
    }, []),
    setScrapeUrl: useCallback((url: string) => {
      dispatch({ type: "SET_SCRAPE_URL", payload: url });
    }, []),
    setAudioFile: useCallback((file: File | null) => {
      dispatch({ type: "SET_AUDIO_FILE", payload: file });
    }, []),
    setDocumentFile: useCallback((file: File | null) => {
      dispatch({ type: "SET_DOCUMENT_FILE", payload: file });
    }, []),
    setImageFiles: useCallback((files: File[]) => {
      dispatch({ type: "SET_IMAGE_FILES", payload: files });
    }, []),
    setVideoFile: useCallback((file: File | null) => {
      dispatch({ type: "SET_VIDEO_FILE", payload: file });
    }, []),
    setVideoUrl: useCallback((url: string) => {
      dispatch({ type: "SET_VIDEO_URL", payload: url });
    }, []),
    setResearchQuery: useCallback((query: string) => {
      dispatch({ type: "SET_RESEARCH_QUERY", payload: query });
    }, []),
    setResearchTitle: useCallback((title: string) => {
      dispatch({ type: "SET_RESEARCH_TITLE", payload: title });
    }, []),
    setSelectedAgentId: useCallback((agentId: string) => {
      dispatch({ type: "SET_SELECTED_AGENT_ID", payload: agentId });
    }, []),
    resetMethodState: useCallback(() => {
      dispatch({ type: "RESET_METHOD_STATE" });
    }, []),
  };
};
